import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'

import { IconSvg, SocialProcessPopup } from 'components/common'
import { ConfirmationModal } from 'components/organisms/modals/confirmation-modal'
import { InstagramVerificationModal } from 'components/organisms/modals/instagram-verification-modal'
import { InstagramSyncButton } from 'containers/common/InstagramSyncButton'
import { useLocalStorage } from 'hooks/useLocalStorage'
import { useStore } from 'hooks/useStore'
import {
	useConnectSocialMutation,
	useConnectSocialNetworkToEventMutation,
	useDisconnectSocialMutation,
} from 'graphql/generated/scheme'
import { trueErrorMessage } from 'utils/error'
import { PIE_PERMISSIONS, SocialScheme } from 'utils/constants'
import { useSearchParams } from 'react-router-dom'
import { Button } from 'components/atoms/button'

// Tells people what Connect will ask of them before they press it; providers not listed use the sign-in popup.
const CONNECT_HINTS: Record<string, string> = {
	[SocialScheme.Instagram]: 'Verify by sending a DM',
}

// required for slight delay in the queue/grains
const DELAY_TIME_MS = 1_000

interface Props {
	socialList: any[]
	onChange?: (...args: any) => any
	iconClassName?: string
	listClassName?: string
	listItemClassName?: string
	buttonClass?: string
	hasSeparator?: boolean
	event?: any
	allowDisconnect?: boolean
	onConnectEvent?: (...args: any) => any
}

export function SocialConnectBlock({
	socialList,
	onChange,
	iconClassName,
	buttonClass,
	hasSeparator = true,
	listClassName,
	listItemClassName,
	event,
	allowDisconnect = true,
	onConnectEvent,
}: Props) {
	const [searchParams] = useSearchParams()
	const a = searchParams.get('a')
	const [openSocialConnectWarning, setOpenSocialConnectWarning] = useState<boolean>(false)
	const [openInstagramVerification, setOpenInstagramVerification] = useState<boolean>(false)
	const { user, refetchUser, selectedPie } = useStore()
	const [currentSocialProvider, setCurrentSocialProvider] = useState<any>(null)
	const [socialProvider, setSocialProvider] = useState<SocialScheme | null>(null)
	const [socialProviderDisconnect, setSocialProviderDisconnect] = useState<any>(null)
	// The account the user asked to disconnect, held until they confirm.
	const [socialToDisconnect, setSocialToDisconnect] = useState<any>(null)
	const [, setSocialConnectToken] = useLocalStorage('socialConnectToken', null)
	const [connectSocial, { data: successConnect, loading: submittingConnect, error: errorConnect }] =
		useConnectSocialMutation()
	const [disconnectSocial, { data: successDisconnect, loading: submittingDisconnect, error: errorDisconnect }] =
		useDisconnectSocialMutation({
			onCompleted: () => {
				refetchUser()
			},
		})
	const [connectSocialNetworkToEvent, { loading: connectSocialNetworkToEventLoading }] =
		useConnectSocialNetworkToEventMutation({
			onCompleted: () => {
				onConnectEvent?.()
			},
			onError: error => {
				toast.error(trueErrorMessage(error))
			},
		})

	useEffect(() => {
		onChange?.(
			user?.socialMedias?.filter((socialItem: any) =>
				socialList.some(item => socialItem.scheme.toLowerCase() === item.scheme)
			) || []
		)
	}, [user])

	// Auto-register social accounts that are already connected in the user's profile
	// but haven't been linked to this specific event yet.
	useEffect(() => {
		if (!event?.eventId || !user?.socialMedias) return
		socialList.forEach((socialItem: any) => {
			const originalScheme = socialItem?.scheme
			if (!originalScheme) return
			const schemeLower = originalScheme.toLowerCase()
			const inProfile = user.socialMedias.some((m: any) => m.scheme.toLowerCase() === schemeLower)
			const inEvent = event.connectedSocialNetworks?.some((s: any) => s.toLowerCase() === schemeLower)
			if (inProfile && !inEvent) {
				connectSocialNetworkToEvent({ variables: { eventId: event.eventId, scheme: originalScheme } })
			}
		})
	}, [event?.eventId, event?.connectedSocialNetworks, user?.socialMedias, socialList])

	useEffect(() => {
		if (socialProvider) {
			setCurrentSocialProvider(socialProvider)
		}
	}, [socialProvider])

	useEffect(() => {
		async function checkConnect() {
			if (successConnect) {
				if (successConnect.user?.socialMedia?.connect) {
					if (event) {
						await connectSocialNetworkToEvent({
							variables: {
								eventId: event.eventId,
								scheme: currentSocialProvider,
							},
						})
					}
				} else {
					toast.error(
						<>
							There's no any connection with your{' '}
							<span className="capitalize">{currentSocialProvider}</span> account. Please try another
							account.
						</>
					)
				}
				refetchUser()
				setCurrentSocialProvider(null)
			}
			if (errorConnect) {
				toast.error(trueErrorMessage(errorConnect.message))
				setCurrentSocialProvider(null)
			}
		}

		checkConnect()
	}, [successConnect, errorConnect])

	useEffect(() => {
		if (successDisconnect) {
			setSocialProviderDisconnect(null)
			// queue delay
			setTimeout(() => {
				refetchUser()
			}, DELAY_TIME_MS)
		}
		if (errorDisconnect) {
			toast.error(trueErrorMessage(errorDisconnect.message))
			setSocialProviderDisconnect(null)
		}
	}, [successDisconnect, errorDisconnect])

	const openSocialConnect = (social: any) => {
		// Instagram connects via DM verification, not the OAuth popup flow
		if (typeof social === 'string' && social.toLowerCase() === SocialScheme.Instagram) {
			setOpenInstagramVerification(true)
			return
		}
		setCurrentSocialProvider(social)
		setOpenSocialConnectWarning(true)
	}

	const _allowDisconnect = !event && allowDisconnect

	// The account handle ("@name") when the provider gives one (TikTok, Instagram), else its display name.
	const getConnectionLabel = (scheme: string) => {
		const connection = user?.socialMedias.find((item: any) => item.scheme.toLowerCase() === scheme.toLowerCase())
		const username = connection?.username?.trim().replace(/^@/, '')
		return username ? `@${username}` : connection?.displayName
	}

	return (
		<>
			<div className={clsx(hasSeparator && 'divide-y divide-white-5', listClassName)}>
				{socialList.map((socialItem: any) => {
					const scheme = socialItem.scheme.toLowerCase()
					const profileSocial = user?.socialMedias?.find((item: any) => item.scheme.toLowerCase() === scheme)
					// With an event, an account counts as connected only once it is also linked to that event.
					const connected =
						!!profileSocial &&
						(!event || !!event.connectedSocialNetworks?.some((item: any) => item.toLowerCase() === scheme))
					const disconnecting = submittingDisconnect && socialProviderDisconnect?.toLowerCase() === scheme

					return (
						<div
							key={socialItem.scheme}
							className={clsx('group flex items-center gap-3 py-3', listItemClassName)}
						>
							<div
								className={clsx(
									'flex shrink-0 items-center justify-center',
									iconClassName ?? 'w-10 h-10 bg-white/10 rounded-full',
									{ 'w-10 h-10 rounded-full !bg-primary': connected }
								)}
							>
								<IconSvg icon={socialItem.icon} />
							</div>
							<div className="min-w-0 flex-1 text-left">
								<div className="truncate text-base">{socialItem.displayName}</div>
								{/* The hint hides on phones: beside Connect it would cut to "Verify by…" */}
								{!connected && CONNECT_HINTS[scheme] && (
									<div className="hidden truncate text-sm leading-5 text-white/50 sm:block">
										{CONNECT_HINTS[scheme]}
									</div>
								)}
								{connected && (
									<div className="flex items-center gap-x-1 text-sm leading-5 text-white/50">
										{/* Pulled left by the glyph's own inset, so every row's check lines up under the name. */}
										<IconSvg
											icon="check"
											width={21}
											height={20}
											className="-ml-1 shrink-0 text-secondary-green"
										/>
										<span className="truncate" title={getConnectionLabel(scheme)}>
											{getConnectionLabel(scheme) ?? 'Connected'}
										</span>
									</div>
								)}
							</div>
							<div className="flex shrink-0 items-center gap-x-2">
								{!connected ? (
									<Button
										variant="secondary"
										aria-label={`Connect ${socialItem.displayName}`}
										className={clsx(buttonClass)}
										onClick={() => {
											openSocialConnect(socialItem.scheme)
										}}
										disabled={
											// (a !== 'create' &&
											// 	selectedPie?.userPermissions.indexOf(
											// 		PIE_PERMISSIONS.CAN_CONNECT_SOCIAL_ACCOUNTS
											// 	) < 0) ||
											submittingConnect || connectSocialNetworkToEventLoading
										}
									>
										{submittingConnect && socialProvider === scheme && (
											<FontAwesomeIcon
												icon={['fas', 'spinner']}
												size="lg"
												spin
												className="mr-1"
											/>
										)}
										Connect
									</Button>
								) : (
									<>
										{/* Always visible: hover-only reveals can't be found on a phone and made the row jump on desktop.
										    Quiet until hovered, and it asks before it disconnects. */}
										{_allowDisconnect && (
											<button
												type="button"
												aria-label={`Disconnect ${socialItem.displayName}`}
												title={`Disconnect ${socialItem.displayName}`}
												className={clsx(
													'flex h-11 min-w-11 items-center justify-center gap-x-1.5 rounded-3xl px-2 text-sm font-bold text-secondary-text transition-colors duration-300 hover:text-secondary-red disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation sm:px-3'
												)}
												onClick={() => setSocialToDisconnect(socialItem)}
												disabled={
													(a !== 'create' &&
														selectedPie?.userPermissions.indexOf(
															PIE_PERMISSIONS.CAN_REMOVE_SOCIAL_ACCOUNTS
														) < 0) ||
													submittingDisconnect
												}
											>
												<FontAwesomeIcon
													icon={disconnecting ? ['fas', 'spinner'] : ['fas', 'link-slash']}
													spin={disconnecting}
												/>
												<span className="hidden sm:inline">Disconnect</span>
											</button>
										)}
										{!event && scheme === SocialScheme.Instagram && (
											<InstagramSyncButton className={clsx(buttonClass)} />
										)}
									</>
								)}
							</div>
						</div>
					)
				})}
			</div>
			<SocialProcessPopup
				action={`verify${
					socialProvider === SocialScheme.Youtube
						? `-${socialProvider}`
						: socialProvider === SocialScheme.AmazonMusic
							? '-music'
							: ''
				}`}
				socialProvider={socialProvider}
				setSocialProvider={setSocialProvider}
				handleProcess={async (socialScheme: any) => {
					const socialConnectToken = JSON.parse(localStorage.getItem('socialConnectToken') as string)
					if (socialConnectToken && socialScheme) {
						await connectSocial({
							variables: { accessKey: socialConnectToken, scheme: socialScheme },
						})
						setSocialConnectToken(null)
					}
				}}
				afterClosePopup={() => {
					// give queue some time to resolve
					setTimeout(() => {
						refetchUser()
					}, DELAY_TIME_MS)
				}}
			/>
			<ConfirmationModal
				title="Connecting your account"
				open={openSocialConnectWarning}
				setOpen={setOpenSocialConnectWarning}
				blur
			>
				<div>
					<div className="mb-8">
						<div>
							You grant permission to Loyalink to use the{' '}
							<span className="capitalize">{currentSocialProvider}</span> User Profile API to read the
							username of the connected <span className="capitalize">{currentSocialProvider}</span>{' '}
							account and provide Loyalink the ability to determine if the requested account is a
							professional or a personal account.
						</div>
						{currentSocialProvider === 'youtube' && (
							<div className="mt-4">
								Loyalink use and transfer to any other app of information received from Google APIs will
								adhere to{' '}
								<a
									className="text-link"
									href="https://developers.google.com/terms/api-services-user-data-policy"
									target="_blank"
									rel="noopener noreferrer"
								>
									Google API Services User Data Policy
								</a>
								, including the Limited Use requirements.
							</div>
						)}
					</div>
					<div className="space-y-2">
						<Button
							variant="secondary"
							fullWidth
							onClick={() => {
								setSocialProvider(currentSocialProvider)
								setOpenSocialConnectWarning(false)
							}}
						>
							Continue
						</Button>
					</div>
				</div>
			</ConfirmationModal>
			<ConfirmationModal
				title={`Disconnect ${socialToDisconnect?.displayName ?? ''}?`}
				open={!!socialToDisconnect}
				setOpen={(open: boolean) => {
					if (!open) setSocialToDisconnect(null)
				}}
				confirmLabel="Disconnect"
				cancelLabel="Keep Connected"
				onCancel={() => setSocialToDisconnect(null)}
				onConfirm={() => {
					const social = socialToDisconnect
					setSocialToDisconnect(null)
					setSocialProviderDisconnect(social.scheme)
					// Failures surface through `errorDisconnect` (toast above), so the rejection needs no handling here
					disconnectSocial({ variables: { scheme: social.scheme } }).catch(() => undefined)
				}}
			>
				<p className="text-center">
					Loyalink will stop showing your {socialToDisconnect?.displayName} account. You can connect it again
					at any time.
				</p>
			</ConfirmationModal>
			<InstagramVerificationModal
				open={openInstagramVerification}
				setOpen={setOpenInstagramVerification}
				onConnected={
					event
						? () => {
								connectSocialNetworkToEvent({
									variables: { eventId: event.eventId, scheme: SocialScheme.Instagram },
								})
							}
						: undefined
				}
			/>
		</>
	)
}
